import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  writeBatch,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

// Fetch all documents
export const fetchData = async (collectionName) => {
  try {
    const colRef = collection(db, collectionName);
    const querySnapshot = await getDocs(colRef);
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    // console.log("Data fetched in fetchData:", data); // Ensure data is logged correctly here
    return data;
  } catch (error) {
    console.error("Error fetching data in fetchData: ", error);
    throw new Error(error.message);
  }
};

// Create a new document
export const createData = async (collectionName, newData) => {
  const colRef = collection(db, collectionName);
  const docRef = await addDoc(colRef, newData);
  if (docRef && docRef.id) {
    // console.log(docRef.id, docRef, "docRef");
    return docRef.id;
  }
};

// Update a document
export const updateData = async (collectionName, { id, updatedData }) => {
  // console.log(collectionName, { id, updatedData }, "in usefire");
  const docRef = doc(db, collectionName, id);
  await updateDoc(docRef, updatedData);
};

// Bulk update documents
// Bulk update data hook
export const bulkUpdateData = async (collectionName, updates) => {
  try {
    const batch = writeBatch(db);
    updates.forEach((updateObj) => {
      const { id, ...updatedData } = updateObj; // Extract the id and the fields to update
      // console.log(id, updatedData, "firebaseOp");
      if (!id || Object.keys(updatedData).length === 0) {
        throw new Error(
          `Invalid update format: { id: ${id}, updatedData: ${JSON.stringify(
            updatedData
          )} }`
        );
      }

      const docRef = doc(db, collectionName, id); // Reference to the document
      batch.update(docRef, updatedData); // Perform batch update with the remaining fields
    });

    await batch.commit(); // Commit the batch operation
    console.log("Batch update successfully committed");
    return updates;
  } catch (error) {
    console.error("Error in bulkUpdateData:", error);
    throw new Error(error.message);
  }
};

// Delete a document
export const deleteData = async (collectionName, id) => {
  const docRef = doc(db, collectionName, id);
  await deleteDoc(docRef);
};

export const getDocsByQuery = async (collectionName, conditions = []) => {
  try {
    const colRef = collection(db, collectionName);
    let firestoreQuery = query(colRef);

    conditions.forEach((condition) => {
      firestoreQuery = query(
        firestoreQuery,
        where(condition.field, condition.operator, condition.value)
      );
    });

    const querySnapshot = await getDocs(firestoreQuery);
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return data;
  } catch (error) {
    console.error("Error fetching data with conditions: ", error);
    throw new Error(error.message);
  }
};

export const bulkCreateData = async (collectionName, data) => {
  // console.log(collectionName, data, "fire");
  const collectionRef = collection(db, collectionName);
  const batch = [];

  data.forEach((doc) => {
    const docRef = addDoc(collectionRef, doc);
    batch.push(docRef, doc);
  });

  await Promise.all(batch);
  return data;
};

export const fetchDocsByIds = async (collectionName, conditions = []) => {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    throw new Error("No conditions provided");
  }

  const collectionRef = collection(db, collectionName);
  let data = [];

  // Normalize conditions to use consistent keys
  const normalizedConditions = conditions.map((cond) => ({
    field: cond.field,
    op: cond.op || cond.operator, // Support both `op` and `operator`
    value: cond.value,
  }));

  const inConditions = normalizedConditions.filter((cond) => cond.op === "in");

  // If there's any 'in' condition, we need to batch those
  if (inConditions.length > 0) {
    const nonInConditions = normalizedConditions.filter((c) => c.op !== "in");

    // Handle each 'in' condition one at a time
    for (const inCond of inConditions) {
      const { field, value: ids } = inCond;
      const batchSize = 10;

      for (let i = 0; i < ids.length; i += batchSize) {
        const batchIds = ids.slice(i, i + batchSize);
        const batchConditions = [
          ...nonInConditions,
          { field, op: "in", value: batchIds },
        ];

        const q = query(
          collectionRef,
          ...batchConditions.map((cond) =>
            where(cond.field, cond.op, cond.value)
          )
        );

        try {
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            data.push({ id: doc.id, ...doc.data() });
          });
        } catch (error) {
          console.error(`Error fetching batch for ${field}:`, error);
          throw new Error(`Failed to fetch documents: ${error.message}`);
        }
      }
    }
  } else {
    // No 'in' condition: normal query
    const filters = normalizedConditions
      .map((cond) => {
        if (cond.field && cond.op && cond.value !== undefined) {
          return where(cond.field, cond.op, cond.value);
        }
        console.warn("Skipping invalid condition:", cond);
        return undefined;
      })
      .filter(Boolean); // remove undefineds

    const q = query(collectionRef, ...filters);

    try {
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() });
      });
    } catch (error) {
      console.error(
        `Error fetching documents for medicine IDs: ${batchIds.join(", ")}`,
        error
      );
      throw new Error(`Failed to fetch inventory documents: ${error.message}`);
    }
  }
  return data;
};

export const getDocumentsSize = async (collectionName, conditions = []) => {
  try {
    const colRef = collection(db, collectionName);
    let firestoreQuery = query(colRef);

    conditions.forEach((condition) => {
      firestoreQuery = query(
        firestoreQuery,
        where(condition.field, condition.operator, condition.value)
      );
    });

    const querySnapshot = await getDocs(firestoreQuery);
    return querySnapshot.size;
    // const data = querySnapshot.docs.map((doc) => ({
    //   id: doc.id,
    //   ...doc.data(),
    // }));
    // console.log(data);
    // return data;
  } catch (error) {
    console.error(`Error fetching data with ${collectionName}: `, error);
    throw new Error(error.message);
  }
};

export const searchPhoneNumbers = async (collectionName, phone) => {
  try {
    const colRef = collection(db, collectionName);
    const phoneQuery = query(
      colRef,
      where("phone", ">=", phone),
      where("phone", "<=", phone + "\uf8ff")
    );
    const querySnapshot = await getDocs(phoneQuery);
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return data;
  } catch (e) {
    console.error("Error fetching phone numbers:", e);
  }
};

export const fetchDocById = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    console.log(docSnap, "docSnap", docSnap.data());
    if (docSnap.exists()) {
      console.log("Document data:", docSnap.data());
      return {
        id: docSnap.id,
        ...docSnap.data(), // Spread the document data to include all fields
      };
    } else {
      console.warn(`No such document: ${collectionName}/${docId}`);
      return null;
    }
  } catch (error) {
    console.error("Error in fetchDocById:", error);
    throw error;
  }
};

export const deleteByPharmacyIdAndMedicineIds = async (
  collectionName,
  pharmacyId,
  medicineIds = []
) => {
  if (!medicineIds.length) return { success: true, deletedCount: 0 };

  // Firestore allows max 10 items in 'in' clause
  const chunks = [];
  for (let i = 0; i < medicineIds.length; i += 10) {
    chunks.push(medicineIds.slice(i, i + 10));
  }

  let totalDeleted = 0;

  for (const chunk of chunks) {
    const q = query(
      collection(db, collectionName),
      where("pharmacyId", "==", pharmacyId),
      where("medicineId", "in", chunk)
    );

    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, collectionName, docSnap.id))
    );

    await Promise.all(deletePromises);
    totalDeleted += snapshot.size;
  }

  return { success: true, deletedCount: totalDeleted };
};
